import { createPath } from 'history';

// Query parameter that carries the destination a user was trying to reach before
// being bounced to the login screen.
export const RETURN_PARAM = 'return';

// Marks a deliberate sign-out. A manual logout navigates to the login screen *before*
// the session teardown flips isLoggedIn, so for a brief window the user still looks
// authenticated. This carries that "I meant to leave" signal so PublicRoute renders
// the login screen instead of bouncing the user back into the app.
//
// It rides the query string rather than history state on purpose: the app uses hash
// history, which silently drops the state object passed to push().
export const LOGGED_OUT_PARAM = 'loggedOut';

// The search string a manual logout redirects to, e.g. `?loggedOut=1`.
export const LOGGED_OUT_SEARCH = `?${LOGGED_OUT_PARAM}=1`;

// True when a location's search string carries the deliberate-sign-out marker.
export const isLoggedOutRedirect = (search) =>
  new URLSearchParams(search).get(LOGGED_OUT_PARAM) === '1';

// Marks a sign-out the *app* decided on: the account is not allowed the page it asked
// for, so it cannot stay signed in here (see views/components/permission-denied). It
// rides alongside LOGGED_OUT_PARAM rather than replacing it — the sign-out half is what
// keeps PublicRoute from bouncing the user back into the app while the teardown is
// still in flight, and this half is what tells the login screen why it is on screen.
export const DENIED_PARAM = 'denied';

// The search string a permission refusal redirects to, e.g. `?loggedOut=1&denied=1`.
export const PERMISSION_DENIED_SEARCH = `?${LOGGED_OUT_PARAM}=1&${DENIED_PARAM}=1`;

// True when a location's search string says the user was refused a page.
export const isPermissionDeniedRedirect = (search) =>
  new URLSearchParams(search).get(DENIED_PARAM) === '1';

// Where to land when there is no usable `return` value.
export const DEFAULT_RETURN_PATH = '/app';

// Extension point: origins a `return` value may point at, in addition to the origin
// serving this app. Add other Sweat440 app origins here to allow returning to them.
//
// NOTE: once this is non-empty, a cross-origin target can no longer be handled by
// `push()` — resolveReturnPath will need to report internal vs external targets and
// the callers will need a `window.location.assign` branch.
export const EXTRA_RETURN_ORIGINS = [];

const isAllowedOrigin = (origin) =>
  origin === window.location.origin || EXTRA_RETURN_ORIGINS.includes(origin);

// Only privated routes live under /app. Restricting return targets to that subtree is
// both the correct invariant and what stops a return value of `/` or `/login` from
// bouncing the user straight back into the login screen.
const isPrivatedPath = (path) => path === '/app' || path.startsWith('/app/');

// Reduces a raw `return` value to an in-app path, or null when it is unusable or
// points somewhere we refuse to send the user.
const toInternalPath = (raw) => {
  const value = (raw || '').trim();
  if (!value) return null;

  let path = null;

  if (value.startsWith('/')) {
    // Reject `//host` and `/\host`, which browsers treat as protocol-relative URLs.
    if (value.startsWith('//') || value.startsWith('/\\')) return null;
    path = value;
  } else {
    let url;
    try {
      url = new URL(value);
    } catch (e) {
      return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!isAllowedOrigin(url.origin)) return null;
    // The app uses hash history, so an absolute URL keeps the in-app path in its hash.
    if (!url.hash.startsWith('#/')) return null;
    path = url.hash.slice(1);
  }

  return isPrivatedPath(path) ? path : null;
};

// Absolute URL of the app shell — everything before the hash-history fragment.
// Derived from the live location so it stays correct when the app is served from a
// subdirectory (as it is on the IIS deploy).
const appBaseUrl = () => `${window.location.origin}${window.location.pathname}`;

// Turns an in-app location into the full absolute URL a user would paste into the
// address bar, e.g. `https://host/#/app/mindbody?foo=bar`.
export const buildReturnUrl = (location) =>
  `${appBaseUrl()}#${createPath(location)}`;

// Builds the search string to hand to the login screen. The `return` value is the
// full absolute URL of the attempted destination, e.g.
// `?return=https%3A%2F%2Fhost%2F%23%2Fapp%2Fmindbody`.
// Returns '' when the location is already the default, to avoid a redundant param.
export const buildReturnSearch = (location) => {
  if (createPath(location) === DEFAULT_RETURN_PATH) return '';
  return `?${RETURN_PARAM}=${encodeURIComponent(buildReturnUrl(location))}`;
};

// Reads `return` out of a location's search string and returns a safe in-app path,
// falling back to the default portal page.
export const resolveReturnPath = (search) => {
  const raw = new URLSearchParams(search).get(RETURN_PARAM);
  return toInternalPath(raw) || DEFAULT_RETURN_PATH;
};
